import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Table, Tag, Button, Modal, Form, Select, message, Popconfirm, Badge } from "antd";
import { useIntegrations, useIntegrationUpsert, useIntegrationDisconnect, useIntegrationDelete } from "../../../api-hooks/integrations";

const INTEGRATION_TYPES = [
  "xero","quickbooks","sage","freeagent","stripe","gocardless","twilio",
  "google_calendar","outlook_calendar","zapier","make","google_maps","companies_house","hmrc_mtd",
];

const STATUS_COLOR: Record<string, string> = {
  connected: "green", disconnected: "default", error: "red", expired: "orange",
};

export default function IntegrationsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [messageApi, ctx] = message.useMessage();

  const { data: rows = [], isLoading } = useIntegrations(cid);
  const upsert = useIntegrationUpsert({ onSuccess: () => { messageApi.success("Saved"); setModalOpen(false); }, onError: () => messageApi.error("Failed") });
  const disconnect = useIntegrationDisconnect({ onSuccess: () => messageApi.success("Disconnected"), onError: () => messageApi.error("Failed") });
  const del = useIntegrationDelete({ onSuccess: () => messageApi.success("Deleted"), onError: () => messageApi.error("Failed") });

  const connectedTypes = new Set(rows.map((r: any) => r.integration_type));

  const columns = [
    { title: "Integration", dataIndex: "integration_type", render: (v: string) => <span className="font-medium capitalize">{v.replace(/_/g, " ")}</span> },
    { title: "Status", dataIndex: "status", render: (v: string) => <Badge status={v === "connected" ? "success" : v === "error" ? "error" : "default"} text={<Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>} /> },
    { title: "Last Sync", dataIndex: "last_sync_at", render: (v: string) => v ? new Date(v).toLocaleString() : "—" },
    { title: "Last Error", dataIndex: "last_error", render: (v: string) => v ? <span className="text-red-500 text-xs">{v.slice(0, 60)}</span> : "—" },
    {
      title: "Actions", key: "actions", render: (_: any, row: any) => (
        <div className="flex gap-2">
          {row.status === "connected" && (
            <Popconfirm title="Disconnect this integration?" onConfirm={() => disconnect.mutate({ cid, id: row.id })}>
              <Button size="small" danger>Disconnect</Button>
            </Popconfirm>
          )}
          <Popconfirm title="Delete this record?" onConfirm={() => del.mutate({ cid, id: row.id })}>
            <Button size="small" type="text" danger>Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Integrations</h2>
          <p className="text-slate-500 text-sm mt-0.5">Company #{cid} — 3rd party connections</p>
        </div>
        <Button type="primary" onClick={() => { form.resetFields(); setModalOpen(true); }}>Add Integration</Button>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal title="Add / Update Integration" open={modalOpen} onCancel={() => setModalOpen(false)}
        onOk={() => form.validateFields().then(v => upsert.mutate({ cid, data: v }))} okText="Save" confirmLoading={upsert.isPending}>
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item name="integration_type" label="Integration Type" rules={[{ required: true }]}>
            <Select placeholder="Select type" options={INTEGRATION_TYPES.map(t => ({ value: t, label: t.replace(/_/g, " ").toUpperCase(), disabled: connectedTypes.has(t) }))} />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="connected">
            <Select options={[
              { value: "connected", label: "Connected" },
              { value: "disconnected", label: "Disconnected" },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
